// Editable row for an IntegratorMappingRule. Normal users compose the condition from
// chips (OperaRuleTokenEditor): entities are picked from a dropdown — never typed —
// AND/OR and parentheses get their own colors, and the UUID predicate is generated
// behind the scenes into item.rule. The { } toggle switches the row to raw predicate
// editing for advanced users; rows whose stored predicate doesn't fit the token
// grammar open in raw mode automatically.
class OperaMappingRuleCell extends UITableViewCell
{
    private nameTextField: MUITextField = null;
    private tokensView: MUIView = null;
    private ruleTextField: MUITextField = null;
    private valueTextField: MUITextField = null;
    private valueComboBox: MUIComboBox = null;
    private modeButton: MUIButton = null;

    private tokenEditor: OperaRuleTokenEditor = null;
    private rawMode = false;
    // The predicate the chips held when the row was switched to raw editing: the way
    // back when the raw text no longer fits the tokens. undefined = the row opened raw.
    private tokenPredicate: string = undefined;
    private discardArmedAt = 0;

    // Entity catalogs for token ↔ id translation and suggestions — set by the VC.
    tokenContext: RuleTokenContext = null;

    // What the value column holds, from the valueType vocabulary: "code" = literal
    // Opera value (free text); "category"/"revenueCenter" = internal vocabulary
    // (picked from valueOptions, not typed); "categorySplit" = CATEGORY:PERCENT pairs
    // validated against valueOptions and a 100 total.
    valueKind: string = "code";
    valueOptions: string[] = [];

    private splitLayer: HTMLElement = null;     // "+ add" affordance (categorySplit)
    // The VC owns the part sub-rows — adding a part changes the row count.
    onSplitStructureChange: () => void = null;

    awakeFromHTML() {
        this.nameTextField = MUIOutlet(this, "name-tf", "MUITextField");
        this.nameTextField.setOnChangeText(this, function(this:OperaMappingRuleCell, tf: MUITextField, value: string) {
            this._item.name = value.trim();
        });

        this.tokensView = MUIOutlet(this, "tokens-view", "MUIView");

        this.ruleTextField = MUIOutlet(this, "rule-tf", "MUITextField");
        this.ruleTextField.setOnChangeText(this, function(this:OperaMappingRuleCell, tf: MUITextField, value: string) {
            let text = value.trim();
            this._item.rule = text.length > 0 ? text : null;
        });

        this.valueTextField = MUIOutlet(this, "value-tf", "MUITextField");
        this.valueTextField.setOnChangeText(this, function(this:OperaMappingRuleCell, tf: MUITextField, value: string) {
            this._item.value = value.trim();
            if (this.valueKind == "categorySplit") this.validateSplit(this._item.value);
        });

        this.valueComboBox = MUIOutlet(this, "value-cb", "MUIComboBox");
        this.valueComboBox.setOnChangeAction(this, function(this:OperaMappingRuleCell, cb: MUIComboBox, value: string) {
            this._item.value = value;
        });

        this.modeButton = MUIOutlet(this, "mode-btn", "MUIButton");
        this.modeButton.setAction(this, function(this:OperaMappingRuleCell) {
            this.toggleMode();
        });

        this.selectionStyle = UITableViewCellSelectionStyle.None;
    }

    private _item: IntegratorMappingRule = null;
    set item(item: IntegratorMappingRule) {
        this._item = item;

        if (this.tokenEditor == null) {
            this.tokenEditor = new OperaRuleTokenEditor();
            this.tokenEditor.init(this.tokensView.layer, this.tokenContext, (friendly) => this.tokensDidChange(friendly));
        }

        this.nameTextField.text = item.name || "";
        this.ruleTextField.text = item.rule || "";
        this.updateValueUI();

        let friendly = OperaRuleExpression.decompile(item.rule, this.tokenContext);
        this.tokenEditor.setTokens(friendly.friendly);
        this.setRawMode(friendly.exact == false);
        this.tokenPredicate = undefined;
        this.discardArmedAt = 0;
        this.setError(null);
    }

    // MARK: - Value column (typed by the vocabulary's valueKind)

    private updateValueUI() {
        let item = this._item;
        let useCombo = this.valueKind == "category" || this.valueKind == "revenueCenter";
        let useSplit = this.valueKind == "categorySplit";

        this.valueTextField.layer.style.display = useCombo || useSplit ? "none" : null;
        this.valueComboBox.layer.style.display = useCombo ? null : "none";
        if (this.splitLayer != null) this.splitLayer.style.display = useSplit ? null : "none";

        if (useSplit) {
            this.buildSplitEditor();
            return;
        }

        if (useCombo) {
            this.valueComboBox.removeAllItems();
            for (let name of this.valueOptions) this.valueComboBox.addItem(name, name);
            if (item.value != null && item.value.length > 0 && this.valueOptions.indexOf(item.value) < 0) {
                this.valueComboBox.addItem(item.value, item.value);
            }
            if (item.value != null && item.value.length > 0) this.valueComboBox.selectItem(item.value);
            return;
        }

        this.valueTextField.text = item.value || "";
        if (this.valueKind == "categorySplit") this.validateSplit(item.value);
        else this.setValueError(null);
    }

    // MARK: - Multi-result values (categorySplit). The parts render as their own
    // indented sub-rows in the table (OperaMappingRuleSplitPartCell) — this cell's
    // value column only offers the "+ add" affordance and carries the whole-split
    // validation state.

    private buildSplitEditor() {
        if (this.splitLayer == null) {
            this.splitLayer = document.createElement("div");
            this.splitLayer.classList.add("rule-split");
            let add = document.createElement("div");
            add.classList.add("btn", "add", "rule-split-add");
            add.title = MIOLocalizeString("ADD_SPLIT_PART", "Add split part");
            add.addEventListener("click", () => {
                let parts = OperaRuleSplit.parts(this._item.value);
                let used = parts.map(p => p.cat);
                let unused = this.valueOptions.filter(c => used.indexOf(c) < 0);
                parts.push({cat: unused.length > 0 ? unused[0] : (this.valueOptions[0] || ""), raw: parts.length == 0 ? "100%" : "*"});
                this._item.value = OperaRuleSplit.serialize(parts);
                if (this.onSplitStructureChange != null) this.onSplitStructureChange();
            });
            this.splitLayer.appendChild(add);
            this.valueTextField.layer.parentElement.appendChild(this.splitLayer);
        }
        this.splitLayer.style.display = null;
        this.validateSplit(this._item.value);
    }

    private validateSplit(text: string) {
        this.setValueError(OperaRuleSplit.validate(text, this.valueOptions));
    }

    private setValueError(error: string) {
        let layer = this.valueKind == "categorySplit" && this.splitLayer != null ? this.splitLayer : this.valueTextField.layer;
        if (error != null) { layer.classList.add("error"); layer.title = error; }
        else { layer.classList.remove("error"); layer.title = ""; }
    }

    // MARK: - Token editing

    private tokensDidChange(friendly: string) {
        if (friendly == null) {
            // Pending chip or stray text — leave the stored rule untouched.
            this.setError(MIOLocalizeString("CONDITION_INCOMPLETE", "condition incomplete"));
            return;
        }
        let result = OperaRuleExpression.compile(friendly, this.tokenContext);
        if (result.error != null) {
            this.setError(result.error);
            return;
        }
        this._item.rule = result.predicate;
        this.setError(null);
    }

    private setError(error: string) {
        let layer = this.tokensView.layer;
        if (error != null) { layer.classList.add("error"); layer.title = error; }
        else { layer.classList.remove("error"); layer.title = this._item != null && this._item.rule != null ? this._item.rule : MIOLocalizeString("ALWAYS_MATCHES_CATCH_ALL", "always matches (catch-all)"); }
    }

    // MARK: - Mode toggle (chips ↔ raw predicate)

    private setRawMode(raw: boolean) {
        this.rawMode = raw;
        this.tokensView.layer.style.display = raw ? "none" : null;
        this.ruleTextField.layer.style.display = raw ? null : "none";
        this.modeButton.layer.classList.toggle("active", raw);
    }

    private toggleMode() {
        if (this.rawMode == false) {
            this.tokenPredicate = this._item.rule || null;
            this.ruleTextField.text = this._item.rule || "";
            this.setRawMode(true);
            return;
        }

        // Back to chips when the predicate round-trips through the tokens.
        let friendly = OperaRuleExpression.decompile(this._item.rule, this.tokenContext);
        if (friendly.exact) {
            this.tokenEditor.setTokens(friendly.friendly);
            this.setRawMode(false);
            this.discardArmedAt = 0;
            this.setError(null);
            return;
        }

        // It doesn't: the raw text is incomplete, or says something the tokens can't.
        // A row that was switched here from chips can always go back to what the chips
        // held — a second press within a few seconds discards the raw edits. A row that
        // opened raw (a stored predicate beyond the grammar) has nothing to go back to.
        let layer = this.ruleTextField.layer;
        let now = Date.now();
        if (this.tokenPredicate !== undefined && this.discardArmedAt > 0 && now - this.discardArmedAt < 4000) {
            this._item.rule = this.tokenPredicate;
            this.ruleTextField.text = this.tokenPredicate || "";
            let back = OperaRuleExpression.decompile(this.tokenPredicate, this.tokenContext);
            this.tokenEditor.setTokens(back.friendly);
            this.setRawMode(false);
            this.discardArmedAt = 0;
            layer.classList.remove("error");
            this.setError(null);
            return;
        }

        this.setRawMode(true);
        layer.classList.add("error");
        if (this.tokenPredicate !== undefined) {
            this.discardArmedAt = now;
            layer.title = MIOLocalizeString("PREDICATE_BEYOND_TOKENS_PRESS_AGAIN", "this text can't be shown as tokens — press { } again to discard it and go back to the tokens");
        }
        else {
            layer.title = MIOLocalizeString("PREDICATE_BEYOND_TOKENS_STAY_RAW", "this predicate can't be expressed with tokens — keep editing it raw");
        }
        setTimeout(() => { layer.classList.remove("error"); }, 4000);
    }
}
